// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the words of the tidy Assistant chat's chrome (header, list drawer, search, card menu,
// History). Kept together so the per-kind labels stay complete.

import type {AssistantDayBucket} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeModel';
import type {AssistantChatKind} from '@fluxer/schema/src/domains/konvo/AssistantChat';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

// Header
export const CHATS_AND_UPDATES = msg({
	message: 'Chats and updates',
	comment:
		'Assistant chat: tooltip of the header button that opens the list of conversations and updates, and the title of that list.',
});
export const NEEDS_YOU_BADGE = msg({
	message: '{count, plural, one {# thing needs you} other {# things need you}}',
	comment: 'Assistant chat: accessible label of the count badge on the list button. count is a number.',
});
export const SEARCH_THIS_CHAT = msg({
	message: 'Search this chat',
	comment: 'Assistant chat: placeholder of the search field in the header.',
});
export const CLEAR_SEARCH = msg({
	message: 'Clear search',
	comment: 'Assistant chat: button that empties the search field.',
});
export const SEARCH_RESULT_COUNT = msg({
	message: '{count, plural, one {# result} other {# results}}',
	comment: 'Assistant chat: number of search results, shown inside the search field. count is a number.',
});
export const NEW_CHAT = msg({
	message: 'New chat',
	comment: 'Assistant chat: header button that starts a fresh conversation with Konvo.',
});
export const CANCEL_SEARCH = msg({message: 'Cancel', comment: 'Assistant chat (phone): leaves search.'});
export const TIDY_UP = msg({
	message: 'Tidy up',
	comment: 'Assistant chat: header menu item that archives finished cards and old conversations at once.',
});
export const TIDY_UP_CARDS = msg({
	message: '{count, plural, one {Archived # finished card} other {Archived # finished cards}}',
	comment: 'Assistant chat: notice after Tidy up archived only cards. count is a number.',
});
export const TIDY_UP_CONVERSATIONS = msg({
	message: '{count, plural, one {Archived # old conversation} other {Archived # old conversations}}',
	comment: 'Assistant chat: notice after Tidy up archived only conversations. count is a number.',
});
export const TIDY_UP_BOTH = msg({
	message:
		'Archived {cards, plural, one {# finished card} other {# finished cards}} and {conversations, plural, one {# old conversation} other {# old conversations}}',
	comment:
		'Assistant chat: notice after Tidy up archived cards and conversations. cards and conversations are numbers.',
});
export const TIDY_UP_DETAIL = msg({
	message: 'They stay in search and Archived.',
	comment: 'Assistant chat: second line of the notice after Tidy up.',
});
export const UNDO = msg({message: 'Undo', comment: 'Assistant chat: button on the notice after archiving.'});
export const DISMISS = msg({
	message: 'Dismiss',
	comment: 'Assistant chat: accessible label of the close button on the notice after archiving.',
});
export const ARCHIVE_FAILED = msg({
	message: 'Could not archive that. Try again.',
	comment: 'Assistant chat: notice when archiving fails.',
});
export const CARD_UPDATE_FAILED = msg({
	message: 'Could not change that. Try again.',
	comment: 'Assistant chat: notice when pinning or muting a card fails.',
});

// List drawer
export const KEEP_OPEN = msg({
	message: 'Keep open',
	comment: 'Assistant chat: tooltip of the pin button that keeps the list open beside the chat.',
});
export const STOP_KEEPING_OPEN = msg({
	message: 'Stop keeping open',
	comment: 'Assistant chat: tooltip of the pin button when the list is kept open beside the chat.',
});
export const CLOSE = msg({message: 'Close', comment: 'Assistant chat: closes the list or History panel.'});
export const VIEW_ALL = msg({message: 'All', comment: 'Assistant chat list: the whole chat.'});
export const VIEW_ALL_DETAIL = msg({
	message: 'Chats and updates',
	comment: 'Assistant chat list: second line under All.',
});
export const VIEW_NEEDS = msg({
	message: 'Needs you',
	comment: 'Assistant chat list: only the things waiting on the user (drafts to send, agent questions).',
});
export const VIEW_UPDATES = msg({
	message: 'Updates',
	comment: 'Assistant chat list: only the cards Konvo posts by itself.',
});
export const VIEW_UPDATES_DETAIL = msg({
	message: 'Agents, meetings, briefs',
	comment: 'Assistant chat list: second line under Updates, examples of what it holds.',
});
export const VIEW_ARCHIVED = msg({message: 'Archived', comment: 'Assistant chat list: archived cards and chats.'});
export const NEEDS_AGENT_QUESTIONS = msg({
	message: '{count, plural, one {Agent question} other {# agent questions}}',
	comment: 'Assistant chat list: part of the line under Needs you. count is a number.',
});
export const NEEDS_DRAFTS = msg({
	message: '{count, plural, one {# draft} other {# drafts}}',
	comment: 'Assistant chat list: part of the line under Needs you. count is a number.',
});
export const NEEDS_OTHER = msg({
	message: '{count, plural, one {# other} other {# others}}',
	comment: 'Assistant chat list: part of the line under Needs you, things that are not drafts or agent questions.',
});
export const LIST_OF_TWO = msg({
	message: '{first}, {second}',
	comment: 'Assistant chat list: joins two parts of the line under Needs you, e.g. "Agent question, 2 drafts".',
});
export const LIST_OF_THREE = msg({
	message: '{first}, {second}, {third}',
	comment: 'Assistant chat list: joins three parts of the line under Needs you.',
});
export const NOTHING_WAITING = msg({
	message: 'Nothing waiting',
	comment: 'Assistant chat list: line under Needs you when nothing waits on the user.',
});
export const ARCHIVE_CONVERSATION = msg({
	message: 'Archive',
	comment: 'Assistant chat list: button on a conversation that archives it.',
});
export const CONVERSATION_ARCHIVED = msg({
	message: 'Conversation archived',
	comment: 'Assistant chat: notice after archiving a conversation; an Undo button follows.',
});
export const RENAME = msg({message: 'Rename', comment: 'Assistant chat list: renames a conversation.'});
export const CONVERSATION_TITLE = msg({
	message: 'Conversation title',
	comment: 'Assistant chat list: accessible label of the field that renames a conversation.',
});
export const NO_CONVERSATIONS = msg({
	message: 'Your chats with Konvo show here.',
	comment: 'Assistant chat list: shown when there are no conversations yet.',
});

const BUCKETS: Record<AssistantDayBucket, MessageDescriptor> = {
	today: msg({message: 'Today', comment: 'Assistant chat: group heading for things from today.'}),
	yesterday: msg({message: 'Yesterday', comment: 'Assistant chat: group heading for things from yesterday.'}),
	week: msg({message: 'This week', comment: 'Assistant chat list: group heading for the days before yesterday.'}),
	earlier: msg({message: 'Earlier', comment: 'Assistant chat: group heading for older things.'}),
};

export function bucketLabel(i18n: I18n, bucket: AssistantDayBucket): string {
	return i18n._(BUCKETS[bucket]);
}

// Search
export const FILTER_EVERYTHING = msg({message: 'Everything', comment: 'Assistant chat search: show every result.'});
export const FILTER_CHATS = msg({
	message: 'Chats',
	comment: 'Assistant chat search: only the user’s messages and Konvo’s replies.',
});
export const FILTER_UPDATES = msg({
	message: 'Updates',
	comment: 'Assistant chat search: only cards Konvo posted by itself.',
});
export const FILTER_DRAFTS = msg({
	message: 'Drafts',
	comment: 'Assistant chat search: only drafts Konvo prepared that are not sent yet.',
});
export const FILTER_LABEL = msg({message: 'Show', comment: 'Assistant chat search: accessible label of the filter.'});
export const ARCHIVED_INCLUDED = msg({
	message: 'Archived included',
	comment: 'Assistant chat search: toggle, on: results include archived cards and chats.',
});
export const INCLUDE_ARCHIVED = msg({
	message: 'Include archived',
	comment: 'Assistant chat search: toggle, off: results leave out archived cards and chats.',
});
export const NEWEST_FIRST = msg({message: 'Newest first', comment: 'Assistant chat search: how results are sorted.'});
export const ASK = msg({
	message: 'Ask',
	comment: 'Assistant chat search: button that sends the search text to Konvo as a question.',
});
export const ASK_KONVO_LABEL = msg({
	message: 'Ask Konvo about “{query}”',
	comment: 'Assistant chat search: accessible label of the Ask button. query is what the user typed.',
});
export const JUMP_TO = msg({
	message: 'Jump to',
	comment: 'Assistant chat search: button on a result that closes search and scrolls to the message.',
});
export const INDEXING = msg({
	message: 'Indexing this chat…',
	comment: 'Assistant chat search: shown while the search index is built; it retries by itself.',
});
export const SEARCHING = msg({message: 'Searching…', comment: 'Assistant chat search: results are loading.'});
export const NO_RESULTS = msg({
	message: 'Nothing found for “{query}”',
	comment: 'Assistant chat search: no results. query is what the user typed.',
});
export const SEARCH_FAILED = msg({
	message: 'Search did not go through. Try again.',
	comment: 'Assistant chat search: the search request failed.',
});
export const ARCHIVED_TAG = msg({message: 'Archived', comment: 'Assistant chat search: tag on an archived result.'});
export const DRAFT_TITLE = msg({
	message: '{title} · draft',
	comment: 'Assistant chat search: title of a result that is a draft Konvo prepared. title is the draft’s title.',
});
export const KONVO_REPLY = msg({
	message: 'Konvo',
	comment: 'Assistant chat search: title of a reply from Konvo that belongs to no named conversation.',
});
export const YOUR_MESSAGE = msg({
	message: 'You',
	comment: 'Assistant chat search: title of the user’s own message that belongs to no named conversation.',
});
export const SEARCH_RESULTS = msg({
	message: 'Search results',
	comment: 'Assistant chat search: accessible label of the results list.',
});

// Card menu and History
export const CARD_OPTIONS = msg({
	message: 'More',
	comment: 'Assistant chat: tooltip of the ⋯ button on a card that opens its menu (History, Pin, Mute, Archive).',
});
export const HISTORY = msg({
	message: 'History',
	comment: 'Assistant chat card menu: shows what this card said over time.',
});
export const HISTORY_TODAY = msg({
	message: '{count, plural, other {# today}}',
	comment: 'Assistant chat card menu: hint beside History, how many updates came today. count is a number.',
});
export const PIN_TO_TOP = msg({
	message: 'Pin to the top',
	comment: 'Assistant chat card menu: keeps this card shown in full above the rest.',
});
export const UNPIN = msg({message: 'Unpin', comment: 'Assistant chat card menu: stops keeping this card at the top.'});
export const ARCHIVE = msg({message: 'Archive', comment: 'Assistant chat card menu: hides this card from the chat.'});
export const UNARCHIVE = msg({
	message: 'Unarchive',
	comment: 'Assistant chat card menu: brings an archived card back into the chat.',
});
export const ARCHIVED_NOTICE = msg({
	message: 'Archived',
	comment: 'Assistant chat: notice after archiving a card; an Undo button follows.',
});
export const ANSWER = msg({
	message: 'Answer',
	comment: 'Assistant chat History: button on a coding agent’s question that opens the thread to answer it.',
});
export const OPEN_AGENTS = msg({message: 'Open Agents', comment: 'Assistant chat History: opens the Agents page.'});
export const HISTORY_EMPTY = msg({
	message: 'Nothing here yet.',
	comment: 'Assistant chat History: shown when the card has no history.',
});
export const HISTORY_LOADING = msg({message: 'Loading…', comment: 'Assistant chat History: loading.'});
export const LIVE = msg({
	message: 'Live',
	comment: 'Assistant chat, phone: after the Agents card title, this card keeps updating in place.',
});

export const LIVE_UPDATED = msg({
	message: 'Live · updated {time}',
	comment:
		'Assistant chat: after the Agents card title, this card keeps updating in place. time is a clock time like 8:50.',
});
export const STATE_DONE = msg({message: 'Done', comment: 'Assistant chat History: a card that was dealt with.'});
export const STATE_EXPIRED = msg({message: 'Expired', comment: 'Assistant chat History: a draft that expired.'});
export const STATE_EARLIER = msg({
	message: 'Earlier',
	comment: 'Assistant chat History: an older copy of a live card.',
});

/** The layout's short state words ("Done", "Expired", "Earlier") in the user's language. */
export function stateLabel(i18n: I18n, state: string | null): string | null {
	if (state === 'Done') return i18n._(STATE_DONE);
	if (state === 'Expired') return i18n._(STATE_EXPIRED);
	if (state === 'Earlier') return i18n._(STATE_EARLIER);
	return state;
}

const HISTORY_TITLES: Record<AssistantChatKind, MessageDescriptor> = {
	agents: msg({message: 'Agents history', comment: 'Assistant chat History panel title for the Agents card.'}),
	agent_usage: msg({
		message: 'Agent plan usage history',
		comment: 'Assistant chat History panel title for the agent plan usage card.',
	}),
	brief: msg({message: 'Morning brief history', comment: 'Assistant chat History panel title for morning briefs.'}),
	agenda: msg({message: 'Daily agenda history', comment: 'Assistant chat History panel title for daily agendas.'}),
	calendar: msg({
		message: 'Meeting reminders history',
		comment: 'Assistant chat History panel title for meeting reminders.',
	}),
	reminder: msg({message: 'Reminders history', comment: 'Assistant chat History panel title for reminders.'}),
	digest: msg({message: 'Daily digest history', comment: 'Assistant chat History panel title for daily digests.'}),
	time: msg({message: 'Time summary history', comment: 'Assistant chat History panel title for time summaries.'}),
	monday: msg({message: 'Monday report history', comment: 'Assistant chat History panel title for Monday reports.'}),
	nudge: msg({message: 'Tips history', comment: 'Assistant chat History panel title for tips from Konvo.'}),
	feedback: msg({message: 'Feedback history', comment: 'Assistant chat History panel title for feedback requests.'}),
	connect: msg({message: 'Connect history', comment: 'Assistant chat History panel title for connect prompts.'}),
	action: msg({message: 'Drafts history', comment: 'Assistant chat History panel title for drafts Konvo prepared.'}),
	dashboard: msg({message: 'Dashboard history', comment: 'Assistant chat History panel title for dashboards.'}),
	other: msg({message: 'Card history', comment: 'Assistant chat History panel title for other cards.'}),
};

export function historyTitle(i18n: I18n, kind: AssistantChatKind | null): string {
	return i18n._(HISTORY_TITLES[kind ?? 'other']);
}

type MutableKind =
	| 'agents'
	| 'agent_usage'
	| 'brief'
	| 'agenda'
	| 'calendar'
	| 'reminder'
	| 'digest'
	| 'time'
	| 'monday'
	| 'nudge'
	| 'feedback';

const MUTE_LABELS: Record<MutableKind, {mute: MessageDescriptor; unmute: MessageDescriptor}> = {
	agents: {
		mute: msg({
			message: 'Mute Agents updates',
			comment: 'Assistant chat card menu: no notifications for Agents updates.',
		}),
		unmute: msg({
			message: 'Unmute Agents updates',
			comment: 'Assistant chat card menu: notify about Agents updates again.',
		}),
	},
	agent_usage: {
		mute: msg({
			message: 'Mute plan usage updates',
			comment: 'Assistant chat card menu: no notifications for agent plan usage.',
		}),
		unmute: msg({
			message: 'Unmute plan usage updates',
			comment: 'Assistant chat card menu: notify about agent plan usage again.',
		}),
	},
	brief: {
		mute: msg({
			message: 'Mute morning briefs',
			comment: 'Assistant chat card menu: no notifications for morning briefs.',
		}),
		unmute: msg({
			message: 'Unmute morning briefs',
			comment: 'Assistant chat card menu: notify about morning briefs again.',
		}),
	},
	agenda: {
		mute: msg({
			message: 'Mute daily agendas',
			comment: 'Assistant chat card menu: no notifications for daily agendas.',
		}),
		unmute: msg({
			message: 'Unmute daily agendas',
			comment: 'Assistant chat card menu: notify about daily agendas again.',
		}),
	},
	calendar: {
		mute: msg({
			message: 'Mute meeting reminders',
			comment: 'Assistant chat card menu: no notifications for meeting reminders.',
		}),
		unmute: msg({
			message: 'Unmute meeting reminders',
			comment: 'Assistant chat card menu: notify about meeting reminders again.',
		}),
	},
	reminder: {
		mute: msg({message: 'Mute reminders', comment: 'Assistant chat card menu: no notifications for reminders.'}),
		unmute: msg({message: 'Unmute reminders', comment: 'Assistant chat card menu: notify about reminders again.'}),
	},
	digest: {
		mute: msg({
			message: 'Mute daily digests',
			comment: 'Assistant chat card menu: no notifications for daily digests.',
		}),
		unmute: msg({
			message: 'Unmute daily digests',
			comment: 'Assistant chat card menu: notify about daily digests again.',
		}),
	},
	time: {
		mute: msg({
			message: 'Mute time summaries',
			comment: 'Assistant chat card menu: no notifications for time summaries.',
		}),
		unmute: msg({
			message: 'Unmute time summaries',
			comment: 'Assistant chat card menu: notify about time summaries again.',
		}),
	},
	monday: {
		mute: msg({
			message: 'Mute Monday reports',
			comment: 'Assistant chat card menu: no notifications for Monday reports.',
		}),
		unmute: msg({
			message: 'Unmute Monday reports',
			comment: 'Assistant chat card menu: notify about Monday reports again.',
		}),
	},
	nudge: {
		mute: msg({message: 'Mute tips', comment: 'Assistant chat card menu: no notifications for tips from Konvo.'}),
		unmute: msg({message: 'Unmute tips', comment: 'Assistant chat card menu: notify about tips from Konvo again.'}),
	},
	feedback: {
		mute: msg({
			message: 'Mute feedback requests',
			comment: 'Assistant chat card menu: no notifications for feedback requests.',
		}),
		unmute: msg({
			message: 'Unmute feedback requests',
			comment: 'Assistant chat card menu: notify about feedback requests again.',
		}),
	},
};

export function muteLabel(i18n: I18n, kind: AssistantChatKind, muted: boolean): string {
	const labels = MUTE_LABELS[kind as MutableKind];
	if (!labels) return '';
	return i18n._(muted ? labels.unmute : labels.mute);
}

/** The line under Needs you, e.g. "Agent question, 2 drafts". */
export function needsYouLine(
	i18n: I18n,
	summary: {total: number; agentQuestions: number; drafts: number; other: number},
): string {
	const parts: Array<string> = [];
	if (summary.agentQuestions) parts.push(i18n._(NEEDS_AGENT_QUESTIONS, {count: summary.agentQuestions}));
	if (summary.drafts) parts.push(i18n._(NEEDS_DRAFTS, {count: summary.drafts}));
	if (summary.other) parts.push(i18n._(NEEDS_OTHER, {count: summary.other}));
	if (parts.length === 0) return i18n._(NOTHING_WAITING);
	if (parts.length === 1) return parts[0];
	if (parts.length === 2) return i18n._(LIST_OF_TWO, {first: parts[0], second: parts[1]});
	return i18n._(LIST_OF_THREE, {first: parts[0], second: parts[1], third: parts[2]});
}

export function tidyUpNotice(i18n: I18n, cards: number, conversations: number): string {
	if (cards && conversations) return i18n._(TIDY_UP_BOTH, {cards, conversations});
	if (conversations) return i18n._(TIDY_UP_CONVERSATIONS, {count: conversations});
	return i18n._(TIDY_UP_CARDS, {count: cards});
}
